import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "reactstrap";
import DeleteIcon from "../Icons/DeleteIcon";
import CrossIcon from "../Icons/CrossIcon";

const DEFAULT_SELECTED_VALUES = [];

const MultiSelectDropdown = ({
  options,
  selectedValues = DEFAULT_SELECTED_VALUES,
  onChange,
  multiSelect = true,
  placeholder = "Select Site",
  setPageStates,
  isLoading = false,
  type = "",
  disabled = false,
  search = false,
  onSearch = null,
  showSelectAll = true,
  showUnSelectAll = true,
  emptyMessage = "No More Data"
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(selectedValues);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // console.log(">>>>>", selectedValues);


  useEffect(() => {
    setSelected(selectedValues || []); // Ensures clearing works when selectedValues becomes []
  }, [selectedValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const debounceTimer = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (onSearch) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        onSearch(val);
      }, 500);
    }
  };

  useEffect(() => {
    if (!multiSelect && selected.length > 1) {
      const firstSelected = selected.slice(0, 1);
      setSelected(firstSelected);
      onChange(firstSelected);
    }
  }, [multiSelect, selected, onChange]);

  const toggleDropdown = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    let updatedSelection;

    if (multiSelect) {
      const alreadySelected = selected.find((item) => item.id === option.id);
      updatedSelection = alreadySelected
        ? selected.filter((item) => item.id !== option.id)
        : [...selected, option];
    } else {
      updatedSelection = selected.some((item) => item.id === option.id)
        ? []
        : [option];
    }

    setSelected(updatedSelection);
    onChange(updatedSelection);

    // Check if all sites are selected and update state
    if (setPageStates) {
      setPageStates((prev) => ({
        ...prev,
        isAssignedAllSite: updatedSelection.length === options.length
      }));
    }
  };

  const handleRemove = (id, item) => {
    const updatedSelection = selected.filter((item) => item.id !== id);
    setSelected(updatedSelection);
    onChange(updatedSelection);

    // Check if all sites are selected and update state
    if (setPageStates) {
      setPageStates((prev) => ({
        ...prev,
        isAssignedAllSite: updatedSelection.length === options.length,
        removeSitesList: [...(prev?.removeSitesList || []), item]
      }));
    }
  };

  // Select All Function
  const selectAll = () => {
    if (options && options.length > 0) {
      setSelected(options);
      onChange(options);

      // Set isAssignedAllSite to true
      if (setPageStates) {
        setPageStates((prev) => ({
          ...prev,
          isAssignedAllSite: true
        }));
      }
    }
  };

  // Unselect All Function
  const unselectAll = () => {
    setSelected([]);
    onChange([]);

    // Set isAssignedAllSite to false
    if (setPageStates) {
      setPageStates((prev) => ({
        ...prev,
        isAssignedAllSite: false
      }));
    }
  };

  // Filter options to exclude already selected ones
  let filteredOptions = options.filter(
    (option) => !selected.some((item) => item.id === option.id)
  );

  if (search && searchTerm) {
    filteredOptions = filteredOptions.filter(option =>
      option.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  return (
    <div className={`multi-select-dropdown1 ${disabled ? "disabled" : ""}`} ref={dropdownRef}>
      {/* Custom Select Box */}
      <div
        className="select-box1"
        onClick={toggleDropdown}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      >
        {selected.length > 0 ? (
          <div className="selected-list1">
            {selected.map((item) => (
              <div key={item.id} className="tag1">
                {item.name}
                {multiSelect && (
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) handleRemove(item.id, item);
                    }}
                    disabled={disabled}
                  >
                    <CrossIcon width="8" height="8" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <span className="placeholder1">{placeholder}</span>
        )}
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div className="dropdown1">
          {multiSelect && (
            <div className="m-1">
              {selected.length === options.length ? (
                showUnSelectAll &&
                <button
                  type="button"
                  className="btn btn-danger  fw-medium py-0"
                  onClick={unselectAll}
                >
                  Unselect All
                </button>

              ) : (
                showSelectAll &&
                <button
                  type="button"
                  className="btn btn-primary text-black fw-medium py-0"
                  onClick={selectAll}
                >
                  Select All
                </button>
              )}
            </div>
          )}

          {search && (
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="dropdown-items1">
            {isLoading ?
              <div className="dropdown-item1">Loading...</div>
              : filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    className="dropdown-item1"
                    onClick={() => handleSelect(option)}
                  >
                    {option.name}
                  </div>
                ))
              ) : (
                <div className="dropdown-item1">{emptyMessage}</div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
